import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import Inputmask from "inputmask";
import { commonService, toasterService } from "../../services";
import "./EncompassPrefill.css";

/* Pull a loan's inputs from Encompass (via the read-only connector layer) and
   populate the quote form so the user can price it themselves. Values are
   filled per-field with provenance:
     encompass  — read from the loan (override -> current-data precedence)
     defaulted  — no value on the loan; the standard default was used
     missing    — needs the user's input before pricing
   The submit path is untouched: pulling never prices, the user reviews first. */

export function EncompassPrefill({ fields, onPull }) {
  const { setValue, register } = useFormContext();
  const [loanNumber, setLoanNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const knownIds = (fields || []).map((f) => f.field_id);

  const pull = () => {
    const ln = loanNumber.trim();
    if (!ln) {
      toasterService.error("Enter an Encompass loan number first.");
      return;
    }
    setBusy(true);
    setResult(null);
    commonService
      .encompassPrefill(ln)
      .then((resp) => {
        const { payload, provenance, warnings, prefillToken, lockDate } = resp.data;
        let applied = 0;
        Object.keys(payload).forEach((fieldId) => {
          // zcode_short / property_state come as {label,value} option objects
          // (their dropdowns load options remotely) — the form-controlled
          // selects render them directly. Only touch fields this user's form
          // actually renders; the server enforces external defaults anyway.
          if (!knownIds.includes(fieldId)) return;
          setValue(fieldId, payload[fieldId], {
            shouldValidate: false,
            shouldDirty: true,
          });
          applied += 1;
        });
        // The signed token binds loanNumber + lock date; the server derives
        // the pricing date from IT, never from anything editable client-side.
        setValue("encompass_prefill_token", prefillToken || "");
        setResult({ provenance, warnings, applied, lockDate, loanNumber: ln });
        if (onPull) onPull({ lockDate: lockDate || null, loanNumber: ln });
        return { warnings, applied };
      })
      .then(({ warnings, applied }) => {
        // re-apply input masks to programmatically-set values
        setTimeout(
          () => Inputmask().mask(document.querySelectorAll("input")),
          0
        );
        const needs = warnings.length;
        if (needs) {
          toasterService.error(
            `Loan ${ln} pulled — ${applied} fields filled, ${needs} need${needs === 1 ? "s" : ""} attention below.`
          );
        } else {
          toasterService.success(
            `Loan ${ln} pulled — ${applied} fields filled. Review, then price.`
          );
        }
      })
      .catch((error) => {
        toasterService.error(
          error?.response?.data?.message ||
            "Couldn't pull that loan from Encompass."
        );
      })
      .finally(() => setBusy(false));
  };

  const counts = result
    ? Object.values(result.provenance).reduce(
        (a, s) => ({ ...a, [s]: (a[s] || 0) + 1 }),
        {}
      )
    : null;

  return (
    <section className="bf-formsection bf-encompass">
      <h6 className="bf-section-title">Pull from Encompass</h6>
      <p className="bf-encompass-hint">
        Enter a loan number to pull in loan&apos;s inputs for an existing
        loan. Nothing is written back to Encompass. You can change inputs to
        try other structures.
      </p>
      <input type="hidden" {...register("encompass_prefill_token")} />
      <div className="bf-encompass-row">
        <input
          className="form-control bf-encompass-input"
          type="text"
          placeholder="Encompass loan number"
          value={loanNumber}
          onChange={(e) => setLoanNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              pull();
            }
          }}
          disabled={busy}
        />
        <button
          type="button"
          className="btn btn-outline-secondary bf-encompass-btn"
          onClick={pull}
          disabled={busy}
        >
          {busy ? "Pulling…" : "Pull loan"}
        </button>
      </div>

      {result && result.lockDate && (
        <div className="bf-encompass-lockdate">
          Loan {result.loanNumber} is locked as of{" "}
          <strong>{result.lockDate}</strong> — this quote will price on that
          day&apos;s rate sheet and its run id will be marked{" "}
          <strong>H</strong> (historical).
        </div>
      )}

      {counts && (
        <div className="bf-encompass-summary">
          {counts.encompass > 0 && (
            <span className="bf-chip bf-chip-loan">
              {counts.encompass} from loan
            </span>
          )}
          {counts.defaulted > 0 && (
            <span className="bf-chip bf-chip-default">
              {counts.defaulted} defaulted
            </span>
          )}
          {counts.missing > 0 && (
            <span className="bf-chip bf-chip-missing">
              {counts.missing} need input
            </span>
          )}
        </div>
      )}

      {result && result.warnings.length > 0 && (
        <ul className="bf-encompass-warnings">
          {result.warnings.map((w, i) => (
            <li key={i}>
              <strong>{w.field}</strong>: {w.message}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default EncompassPrefill;
